import { useCallback, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { isEqual } from 'lodash-es';
import { CallRouteV1 } from '@frontend/api-call-route';
import { useTranslation } from '@frontend/i18n';
import { breakpoints, useTargetElementContainerQuery } from '@frontend/responsiveness';
import { useModalControl } from '@frontend/design-system';
import { CalendarToggle } from '../../../phone-hours/calendar-toggle';
import CalendarView from '../../../phone-hours/calendar-view';
import { PhoneHoursForm } from '../../../phone-hours/phone-hours-form/phone-hours-form';
import { usePhoneHoursForm } from '../../../phone-hours/phone-hours-form/use-phone-hours-form';
import { SwitchToLargeModal } from '../../../phone-hours/switch-to-large-modal';
import { Step } from './atoms/step';
import { AddStepPanelProps } from './types';

export type PhoneHoursStepProps = AddStepPanelProps<CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_HOURS>;

type ExtendedPhoneHoursStepProps = PhoneHoursStepProps & {
  isCalendarViewOpen?: boolean;
  onCalendarViewChange?: (isOpen: boolean) => void;
};

export const PhoneHoursStep = ({
  initialState,
  onProceedClick,
  isCalendarViewOpen,
  onCalendarViewChange,
}: ExtendedPhoneHoursStepProps) => {
  const { t } = useTranslation('phone');
  const mainContentElement = useMemo(() => document.querySelector('#main-content') as HTMLElement, []);
  const [isSmallMode] = useTargetElementContainerQuery({
    maxWidth: breakpoints.medium.max,
    element: mainContentElement,
  });
  const { formProps, getPhoneHours } = usePhoneHoursForm({
    initialName: initialState?.callObject?.primitiveName ?? '',
    phoneHours: initialState?.callObject?.phoneHoursExpansion?.phoneHours ?? [],
    keepNewIds: true,
  });

  const [showCalendarView, setShowCalendarView] = useState(isCalendarViewOpen ?? false);
  const unsupportedCalendarModal = useModalControl();

  // Sync calendar view state with prop changes from parent
  useEffect(() => {
    if (isCalendarViewOpen !== undefined) {
      setShowCalendarView(isCalendarViewOpen);
    }
  }, [isCalendarViewOpen]);

  const handleShowCalendarClick = () => {
    if (isSmallMode) {
      unsupportedCalendarModal.openModal();
      return;
    }

    setShowCalendarView((prev) => {
      const next = !prev;
      onCalendarViewChange?.(next);
      return next;
    });
  };

  const nameValue = formProps.nameForm.values.name ?? '';
  const isNameFieldEmpty = nameValue === '';
  const normalizePhoneHours = (phoneHours: any[]) =>
    phoneHours.slice().sort((a, b) => a.scheduleId.localeCompare(b.scheduleId));
  const hasEmptyBreakName = Object.values(formProps.breakSchedules).some(
    (breakSchedule) => breakSchedule.name.trim() === ''
  );

  const hasChanges = () => {
    const currentPhoneHours = getPhoneHours();
    const initialPhoneHours = initialState?.callObject?.phoneHoursExpansion?.phoneHours ?? [];

    const nameChanged = nameValue !== initialState?.callObject?.primitiveName;

    const phoneHoursChanged = !isEqual(normalizePhoneHours(currentPhoneHours), normalizePhoneHours(initialPhoneHours));

    return nameChanged || phoneHoursChanged;
  };

  const handleProceedClick = useCallback(() => {
    const phoneHours = getPhoneHours();

    onProceedClick({
      type: CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_HOURS,
      callObject: {
        primitiveId: initialState?.callObject.primitiveId ?? '',
        primitiveName: nameValue,
        instructionId: initialState?.callObject.instructionId ?? '',
        instructionSetId: initialState?.callObject.instructionSetId ?? '',
        phoneHoursExpansion: {
          phoneHours,
        },
      },
    });
  }, [
    getPhoneHours,
    onProceedClick,
    initialState?.callObject.primitiveId,
    initialState?.callObject.instructionId,
    initialState?.callObject.instructionSetId,
    nameValue,
  ]);

  return (
    <Step
      title={t('Phone Hours')}
      description={t('Define Open, Closed, and Break hours in order to route calls differently by time of day.')}
      primaryButtonDisabled={!hasChanges() || isNameFieldEmpty || hasEmptyBreakName}
      onPrimaryButtonClick={handleProceedClick}
    >
      <CalendarToggle
        showCalendarView={showCalendarView}
        onToggle={handleShowCalendarClick}
        hasPermission={true}
        isSmallMode={isSmallMode}
      />
      <div
        css={css`
          display: flex;
          gap: 24px;
          align-items: flex-start;
          width: 100%;
          min-width: 0;
        `}
      >
        {showCalendarView && !isSmallMode && <CalendarView phoneHours={getPhoneHours()} />}
        <PhoneHoursForm {...formProps} routingActionsEnabled showHeader={false} />
      </div>
      <SwitchToLargeModal {...unsupportedCalendarModal.modalProps} />
    </Step>
  );
};
